import { Blocks } from '@/types/application';
import { BackendNode } from '@/types/backendNode';
import { Detail } from '@/types/detail';
import { Metadata } from '@/types/metadata/metadata';
import { Subroutine } from '@/types/subroutine';
import { isNumber, isString } from './typeGuards';

export const isStartBlock = (node: BackendNode | string) => {
  const type = isString(node) ? node : node.type;
  return type === 'APIStart' || type === 'EventStart' || type === 'SignalStart' || type === 'SubStart';
};

// The type of a subroutine's start: a link mirrors its target's in link.start,
// while a local subroutine reads its start block's type.
export const startType = (subroutine: Subroutine, blocks: Blocks): string | undefined => {
  if (subroutine.link) return subroutine.link.start;
  return isNumber(subroutine.startblock) ? blocks.nodes[subroutine.startblock]?.type : undefined;
};

// The engine dispatches app entry to the appstart block, so an application may hold at most one.
export const findAppStartNodes = (nodes: Blocks['nodes'], metadata: Metadata): number[] =>
  Object.entries(nodes)
    .filter(([, node]) => metadata.blocks.find((block) => block.type === node.type)?.style === 'appstart')
    .map(([nodeId]) => Number.parseInt(nodeId));

// The app entry is the subroutine holding the appstart block; an AppReturn returns through its interface.
export const findEntrySubroutine = (
  subroutines: Subroutine[],
  nodes: Blocks['nodes'],
  metadata: Metadata
): Subroutine | undefined => {
  const [entryNode] = findAppStartNodes(nodes, metadata);
  return subroutines.find((subroutine) => subroutine.nodes.includes(entryNode));
};

export const findStartblock = (nodes: number[], blocks: Blocks, metadata: Metadata): number | undefined =>
  nodes.findLast((node) => {
    const style = metadata.blocks.find((block) => block.type === blocks.nodes[node]?.type)?.style;
    return style === 'subroutine' || style === 'appstart';
  });

export const stripEmptyDetails = (details: Record<string, Detail>): Record<string, Detail> =>
  Object.fromEntries(Object.entries(details).filter(([, detail]) => Object.keys(detail).length));
